// Follow-ups — the rental-reference row's two additions (WEL-181):
//   • a BADGE for where the household stands (`rv.applicationStage`), and
//   • a CANCEL for the reference, so a reference staff no longer need stops
//     getting reminders.
//
// Pure helpers, no module imports beyond types, so the table and its tests need
// no API mocks. The page injects the real `cancelRequests`.
//
// The id rule (docs/no-ids-in-ui.md): the cancel id is derived from the row's
// `key` and goes straight to the request body. It is never rendered, never put
// in an aria-label, a title or a toast, and a server reason that happens to
// quote it has it scrubbed before a person reads it.
//
// ALL user-facing strings here are DRAFT (docs/design/wel-181-182-notes/W2-NOTES.md).

import type { FollowUpItem, FollowUpsResponse } from './followUpsClient';
import type { CancelResponse } from './requestsClient';

// ---------------------------------------------------------------------------
// Badge
// ---------------------------------------------------------------------------

export interface StageBadge {
  /** DRAFT: "Approved" / "Tenant now". */
  label: string;
  /** The design system's badge tone. */
  tone: 'success' | 'info';
}

/**
 * The badge a reference row shows for `rv.applicationStage`. `null` (or a value
 * this build does not know, or an API from before the field) shows nothing.
 */
export function applicationStageBadge(stage: unknown): StageBadge | null {
  if (stage === 'approved') return { label: 'Approved', tone: 'success' };
  if (stage === 'tenant') return { label: 'Tenant now', tone: 'info' };
  return null;
}

// ---------------------------------------------------------------------------
// Cancel: the id
// ---------------------------------------------------------------------------

/**
 * The request id `POST /api/requests/cancel` wants for a reference row:
 * `rv#<groupId>#<sk>` → `rental_verification:<groupId>:<sk>`. Split on the FIRST
 * TWO `#` only (the sk is a normalized address and keeps whatever it holds).
 * The server splits the id on `:` with the groupId as the second part, so a
 * groupId carrying a colon would be misread — such a key is refused here (null)
 * rather than cancelling the wrong thing. Null for any key that is not a
 * reference row's.
 */
export function referenceRequestId(key: string): string | null {
  const first = key.indexOf('#');
  if (first < 0 || key.slice(0, first) !== 'rv') return null;
  const second = key.indexOf('#', first + 1);
  if (second < 0) return null;
  const groupId = key.slice(first + 1, second);
  const sk = key.slice(second + 1);
  if (!groupId.trim() || !sk.trim()) return null;
  if (groupId.includes(':')) return null;
  return `rental_verification:${groupId}:${sk}`;
}

/** True when the row is a reference row that can be cancelled. */
export function canCancelReference(item: Pick<FollowUpItem, 'key' | 'kind'>): boolean {
  return item.kind === 'rv' && referenceRequestId(item.key) !== null;
}

// ---------------------------------------------------------------------------
// Cancel: the words
// ---------------------------------------------------------------------------

/** DRAFT: the row action. */
export const CANCEL_REFERENCE_TEXT = 'Cancel reference';
/** DRAFT: the confirm button. The dialog's own dismiss button reads "Cancel", so this one says more. */
export const CANCEL_REFERENCE_CONFIRM_TEXT = 'Yes, cancel reference';
/** DRAFT */
export const CANCEL_REFERENCE_BUSY_TEXT = 'Cancelling…';
/** DRAFT: what the landlord is called when the reference has no name for them. */
export const UNNAMED_LANDLORD_TEXT = 'The previous landlord';
/** DRAFT: the toast after a successful cancel. */
export const REFERENCE_CANCELLED_TEXT = 'Reference cancelled.';

/**
 * The landlord as the confirm names them: the name on the reference, else a
 * plain stand-in. Never an id, never the row key.
 */
export function landlordForConfirm(item: Pick<FollowUpItem, 'rv'>): string {
  return item.rv?.landlord.name.trim() || UNNAMED_LANDLORD_TEXT;
}

/**
 * DRAFT (build sheet): "Cancel this reference? {Landlord} stops getting
 * reminders. If they open the link, they'll see the request was withdrawn."
 * The title carries the question, the description the consequence.
 */
export function cancelReferenceCopy(landlord: string): { title: string; description: string } {
  const who = landlord.trim() || UNNAMED_LANDLORD_TEXT;
  return {
    title: 'Cancel this reference?',
    description: `${who} stops getting reminders. If they open the link, they'll see the request was withdrawn.`,
  };
}

// ---------------------------------------------------------------------------
// Cancel: the result
// ---------------------------------------------------------------------------

const COULDNT = "Couldn't cancel the reference.";

/**
 * `POST /api/requests/cancel` answers HTTP 200 even when it refused the cancel
 * (`failed: 1`, `results[0].ok: false`, a `reason`) — so a resolved promise is
 * not a success. Null when the cancel went through; otherwise the sentence to
 * show, carrying the server's reason. A response that says nothing sure
 * (missing or malformed) is a failure too: never claim a cancel nobody confirmed.
 */
export function cancelFailure(
  res: Partial<CancelResponse> | null | undefined,
  requestId?: string,
): string | null {
  const results = res && Array.isArray(res.results) ? res.results : [];
  const refused = results.find((r) => r.ok !== true);
  const confirmed =
    res != null && res.failed === 0 && typeof res.cancelled === 'number' && res.cancelled >= 1 && !refused;
  if (confirmed) return null;
  let reason = (refused?.reason ?? results.find((r) => r.reason)?.reason ?? '').trim();
  if (reason && requestId) reason = reason.split(requestId).join('this reference').trim();
  return reason ? `Couldn't cancel the reference: ${reason}` : COULDNT;
}

/** A cancel the server refused (HTTP 200 with a failure) — the message is ready to show. */
export class ReferenceCancelError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ReferenceCancelError';
  }
}

/** The shape of `cancelRequests` (injected so this file imports no API module). */
export type CancelFn = (
  ids: string[],
  opts: { notify: boolean; message?: string },
) => Promise<CancelResponse>;

/**
 * Cancel the reference behind a call-list row: quietly (`notify: false` — the
 * landlord is not emailed; their link shows the withdrawn page) and strictly
 * (throws `ReferenceCancelError` with the server's reason when the response says
 * the cancel did not happen). A transport/HTTP failure from `cancel` propagates
 * unchanged.
 */
export async function cancelReference(key: string, cancel: CancelFn): Promise<void> {
  const id = referenceRequestId(key);
  if (!id) throw new ReferenceCancelError(COULDNT);
  const res = await cancel([id], { notify: false });
  const failure = cancelFailure(res, id);
  if (failure) throw new ReferenceCancelError(failure);
}

// ---------------------------------------------------------------------------
// The list after a cancel
// ---------------------------------------------------------------------------

/**
 * The list without one row — shown the moment a cancel is confirmed, so the
 * row does not linger while the reload is out (or if it fails). The reload then
 * replaces it with the server's list.
 */
export function withoutFollowUp(list: FollowUpsResponse, key: string): FollowUpsResponse {
  const keep = (items: FollowUpItem[] | undefined) => (items ?? []).filter((i) => i.key !== key);
  return { ...list, due: keep(list.due), snoozed: keep(list.snoozed), done: keep(list.done) };
}

/** The row with this key, in any of the three lists. */
export function findFollowUp(list: FollowUpsResponse, key: string): FollowUpItem | undefined {
  return [...list.due, ...list.snoozed, ...(list.done ?? [])].find((i) => i.key === key);
}
